import React, { useState, useEffect } from "react";
import api from "../../config/axios";
import "./Dashboard.css";
import dashboardService from "../../services/dashboard";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { MdArrowForward, MdClose, MdDownload, MdFilterList, MdRefresh } from "react-icons/md";
import { format, startOfMonth, endOfMonth } from "date-fns";
import useUserStore from "../../store/useUserStore";
import LoadingSpinner from "../common/LoadingSpinner";
import ShadcnActionDropdown from "../common/ShadcnActionDropdown/ShadcnActionDropdown";

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdvancedReport, setShowAdvancedReport] = useState(false);
  const [showDeviceTrafficModal, setShowDeviceTrafficModal] = useState(false);
  const [showOrdersTrendModal, setShowOrdersTrendModal] = useState(false);
  const [showCartAnalyticsModal, setShowCartAnalyticsModal] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('month');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useUserStore();

  const toNumber = (value) => {
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : 0;
  };

  const safeFixed = (value, digits = 2) => toNumber(value).toFixed(digits);

  const safeLocale = (value) => toNumber(value).toLocaleString();
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(toNumber(value));

  const percent = (part, total, digits = 1) => {
    const denominator = toNumber(total);
    if (denominator <= 0) {
      return (0).toFixed(digits);
    }

    return ((toNumber(part) / denominator) * 100).toFixed(digits);
  };

  const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  useEffect(() => {
    fetchDashboardData();

    // Set up real-time data refresh every 30 seconds
    const interval = setInterval(() => {
      refreshDashboardData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch comprehensive dashboard data from the backend
      const response = await dashboardService.getComprehensiveDashboard();

      console.log('Raw dashboard response:', response);

      if (!response) {
        throw new Error("No dashboard data received");
      }

      // The response is already the data object, not wrapped in .data
      const dashboardData = response;

      console.log('Dashboard data received:', dashboardData);
      console.log('Recent orders type:', typeof dashboardData.recentOrders, 'Value:', dashboardData.recentOrders);
      console.log('Top products type:', typeof dashboardData.topProducts, 'Value:', dashboardData.topProducts);
      console.log('Vendor performance type:', typeof dashboardData.vendorPerformance, 'Value:', dashboardData.vendorPerformance);

      // Transform the data to match our component structure
      const transformedData = {
        // Revenue and financial data
        totalRevenue: dashboardData.totalRevenue || 0,
        monthlyRevenue: dashboardData.monthlyRevenue || 0,
        averageOrderValue: dashboardData.averageOrderValue || 0,
        abandonedRevenue: dashboardData.abandonedRevenue || 0,

        // Orders data
        orders: dashboardData.orders || 0,
        monthlyOrders: dashboardData.monthlyOrders || 0,
        completedOrders: dashboardData.completedOrders || 0,
        pendingOrders: dashboardData.pendingOrders || 0,

        // User data
        activeUsers: dashboardData.activeUsers || 0,
        newUsersThisMonth: dashboardData.newUsersThisMonth || 0,
        allVendors: dashboardData.allVendors || 0,
        allBuyers: dashboardData.allBuyers || 0,
        allStaff: dashboardData.allStaff || 0,

        // Product data
        products: dashboardData.products || 0,

        // Cart analytics
        conversionRate: dashboardData.conversionRate || 0,
        abandonedCarts: dashboardData.abandonedCarts || 0,

        // Chart data - ensure proper format
        revenueChart: dashboardData.revenueChart || [],
        ordersChart: dashboardData.ordersChart ? dashboardData.ordersChart.map(item => ({
          date: item.name,
          value: item.value
        })) : [],

        // Device traffic data
        deviceTraffic: dashboardData.deviceTraffic || {},

        // Table data - fetch real-time data for these
        recentOrders: await fetchRecentOrders(),
        topProducts: await fetchTopProducts(),
        vendorPerformance: await fetchVendorPerformance()
      };

      // Ensure all required fields are present
      if (!transformedData.recentOrders || transformedData.recentOrders.length === 0) {
        console.log('No recent orders data, using empty array');
        transformedData.recentOrders = [];
      }

      if (!transformedData.topProducts || transformedData.topProducts.length === 0) {
        console.log('No top products data, using empty array');
        transformedData.topProducts = [];
      }

      if (!transformedData.vendorPerformance || transformedData.vendorPerformance.length === 0) {
        console.log('No vendor performance data, using empty array');
        transformedData.vendorPerformance = [];
      }

      console.log('Transformed data:', transformedData);

      setDashboardData(transformedData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setError("Failed to load dashboard data. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Real-time data refresh function
  const refreshDashboardData = async () => {
    try {
      setIsRefreshing(true);
      const response = await dashboardService.refreshDashboardData();
      if (response) {
        const dashboardData = response?.data || response;

        setDashboardData(prevData => ({
          ...(prevData || {}),
          // Update real-time metrics
          totalRevenue: dashboardData.totalRevenue ?? prevData?.totalRevenue ?? 0,
          monthlyRevenue: dashboardData.monthlyRevenue ?? prevData?.monthlyRevenue ?? 0,
          orders: dashboardData.orders ?? prevData?.orders ?? 0,
          monthlyOrders: dashboardData.monthlyOrders ?? prevData?.monthlyOrders ?? 0,
          pendingOrders: dashboardData.pendingOrders ?? prevData?.pendingOrders ?? 0,
          completedOrders: dashboardData.completedOrders ?? prevData?.completedOrders ?? 0,
          activeUsers: dashboardData.activeUsers ?? prevData?.activeUsers ?? 0,
          allVendors: dashboardData.allVendors ?? prevData?.allVendors ?? 0,
          allBuyers: dashboardData.allBuyers ?? prevData?.allBuyers ?? 0,
          allStaff: dashboardData.allStaff ?? prevData?.allStaff ?? 0,
          products: dashboardData.products ?? prevData?.products ?? 0,
          conversionRate: dashboardData.conversionRate ?? prevData?.conversionRate ?? 0,
          abandonedCarts: dashboardData.abandonedCarts ?? prevData?.abandonedCarts ?? 0,
          abandonedRevenue: dashboardData.abandonedRevenue ?? prevData?.abandonedRevenue ?? 0,
          averageOrderValue: dashboardData.averageOrderValue ?? prevData?.averageOrderValue ?? 0,
          newUsersThisMonth: dashboardData.newUsersThisMonth ?? prevData?.newUsersThisMonth ?? 0,
          // Update charts
          revenueChart: dashboardData.revenueChart ?? prevData?.revenueChart ?? [],
          ordersChart: dashboardData.ordersChart ?? prevData?.ordersChart ?? [],
          deviceTraffic: dashboardData.deviceTraffic ?? prevData?.deviceTraffic ?? {}
        }));

        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Real-time refresh error:", error);
      // Don't show error for background refresh
    } finally {
      setIsRefreshing(false);
    }
  };

  // Manual refresh function
  const handleManualRefresh = async () => {
    await refreshDashboardData();
  };

  // Fetch real-time recent orders
  const fetchRecentOrders = async () => {
    try {
      const response = await dashboardService.getRecentOrders(8);
      console.log('Recent orders response:', response);
      return normalizeList(response);
    } catch (error) {
      console.error("Error fetching recent orders:", error);
      // Return empty array if API endpoint doesn't exist yet
      return [];
    }
  };

  // Fetch real-time top products
  const fetchTopProducts = async () => {
    try {
      const response = await dashboardService.getTopProducts(5);
      console.log('Top products response:', response);
      return normalizeList(response);
    } catch (error) {
      console.error("Error fetching top products:", error);
      // Return empty array if API endpoint doesn't exist yet
      return [];
    }
  };

  // Fetch real-time vendor performance
  const fetchVendorPerformance = async () => {
    try {
      const response = await dashboardService.getVendorPerformance(5);
      console.log('Vendor performance response:', response);
      return normalizeList(response);
    } catch (error) {
      console.error("Error fetching vendor performance:", error);
      // Return empty array if API endpoint doesn't exist yet
      return [];
    }
  };

  const handleAdvancedReport = () => {
    setShowAdvancedReport(true);
  };

  const handleDeviceTrafficMore = () => {
    setShowDeviceTrafficModal(true);
  };

  const handleOrdersTrendMore = () => {
    setShowOrdersTrendModal(true);
  };

  const handleDownloadReport = async (type) => {
    try {
      setLoading(true);
      const blob = await dashboardService.exportDashboardData(type, selectedDateRange);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_report_${selectedDateRange}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert(`${type} report downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading report:", error);
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (range) => {
    setSelectedDateRange(range);
    // You can implement date range filtering here
    console.log('Date range changed to:', range);
  };

  if (loading)
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <div className="date-range">Loading...</div>
        </div>
        <LoadingSpinner
          size="large"
          message="Loading dashboard data..."
        />
      </div>
    );

  if (error)
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
        </div>
        <div className="error-state">
          <LoadingSpinner
            size="medium"
            message={`Error: ${error}`}
            color="danger"
          />
        </div>
      </div>
    );

  if (!dashboardData)
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
        </div>
        <div className="error-state">
          <LoadingSpinner
            size="medium"
            message="No data available"
          />
        </div>
      </div>
    );

  // Get current date range
  const currentDate = new Date();
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const dateRange = `${format(monthStart, "MMM dd")} - ${format(
    monthEnd,
    "MMM dd, yyyy"
  )}`;

  // Real data for the stats cards
  const statsData = [
    {
      title: "Total Revenue",
      value: formatCurrency(dashboardData.totalRevenue),
      change: `+${percent(dashboardData.monthlyRevenue, dashboardData.totalRevenue)}%`,
      trend: "up",
      color: "#FFA500",
    },
    {
      title: "Total Orders",
      value: safeLocale(dashboardData.orders),
      change: `+${percent(dashboardData.monthlyOrders, dashboardData.orders)}%`,
      trend: "up",
      color: "#00FF00",
    },
    {
      title: "Active Users",
      value: safeLocale(dashboardData.activeUsers),
      change: `+${percent(dashboardData.newUsersThisMonth, dashboardData.activeUsers)}%`,
      trend: "up",
      color: "#0088FE",
    },
    {
      title: "Products",
      value: safeLocale(dashboardData.products),
      change: `+${percent(dashboardData.allVendors, dashboardData.products)}%`,
      trend: "up",
      color: "#FF6B6B",
    },
  ];

  // Real data for bar chart
  const barData = dashboardData.revenueChart || [];

  // Real data for pie chart (device traffic)
  const pieData = dashboardData.deviceTraffic ?
    Object.entries(dashboardData.deviceTraffic).map(([key, value], index) => ({
      name: key,
      value: value,
      color: ["#FF0000", "#FFA500", "#0088FE"][index] || "#00C4FF"
    })) : [];

  // Real data for traffic chart
  const trafficData = dashboardData.ordersChart || [];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard</h1>
          <div className="real-time-indicator">
            <span className={`status-dot ${isRefreshing ? 'refreshing' : 'live'}`}></span>
            <span className="status-text">
              {isRefreshing ? 'Refreshing...' : 'Live Data'}
            </span>
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
        </div>
        <div className="header-right">
          <div className="date-range">{dateRange}</div>
          <button
            className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            title="Refresh dashboard data"
          >
            <MdRefresh />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Quick Navigation Section */}
      <div className="quick-navigation">
        <h3>Quick Access</h3>
        <div className="nav-buttons">
          <button
            className="nav-btn primary"
            onClick={() => console.log('Navigate to orders page')}
          >
            📋 View All Orders
          </button>
          <button
            className="nav-btn secondary"
            onClick={() => console.log('Navigate to products page')}
          >
            📦 View All Products
          </button>
          <button
            className="nav-btn secondary"
            onClick={() => console.log('Navigate to vendors page')}
          >
            🏢 View All Vendors
          </button>
          <button
            className="nav-btn secondary"
            onClick={() => console.log('Navigate to staff page')}
          >
            👥 View All Staff
          </button>
        </div>
      </div>

      <div className="stats-grid">
        {statsData.map((stat, index) => (
          <div className="stat-card" key={index}>
            <div className="stat-header">
              <span>{stat.title}</span>
              <span className={`stat-change ${stat.trend}`}>{stat.change}</span>
            </div>
            <div className="stat-value">{stat.value}</div>
            <LineChart width={100} height={30} data={trafficData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={stat.color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </div>
        ))}
      </div>

      <div className="charts-grid">
        <div className="chart-container">
          <div className="chart-header">
            <h2>Revenue Overview</h2>
            <button className="advanced-btn" onClick={handleAdvancedReport}>
              Advanced Report <MdArrowForward />
            </button>
          </div>
          <BarChart width={800} height={300} data={barData}>
            <XAxis dataKey="date" />
            <YAxis />
            <Bar dataKey="value" fill="#FFA500" />
          </BarChart>
        </div>

        <div className="chart-container cart-section">
          <div className="chart-header">
            <h2>Cart Analytics</h2>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="More"
              actions={[
                {
                  label: 'View Cart Analytics',
                  icon: 'view',
                  onClick: () => setShowCartAnalyticsModal(true),
                  tooltip: 'View detailed cart analytics'
                },
                {
                  label: 'Export Data',
                  icon: 'download',
                  onClick: () => handleDownloadReport('cart-analytics'),
                  tooltip: 'Export cart analytics data'
                }
              ]}
            />
          </div>
          <div className="percentage-indicator">
            <span>{safeFixed(dashboardData.conversionRate, 1)}%</span>
          </div>
          <div className="cart-stats">
            <div className="cart-stat">
              <span>Abandoned Cart</span>
              <span>{safeLocale(dashboardData.abandonedCarts)}</span>
            </div>
            <div className="cart-stat">
              <span>Abandoned Revenue</span>
              <span>{formatCurrency(dashboardData.abandonedRevenue)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bottom-charts">
        <div className="chart-container">
          <div className="chart-header">
            <h2>Device Traffic</h2>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="More"
              actions={[
                {
                  label: 'View Device Traffic',
                  icon: 'view',
                  onClick: handleDeviceTrafficMore,
                  tooltip: 'View detailed device traffic'
                },
                {
                  label: 'Export Data',
                  icon: 'download',
                  onClick: () => handleDownloadReport('device-traffic'),
                  tooltip: 'Export device traffic data'
                }
              ]}
            />
          </div>
          <PieChart width={300} height={300}>
            <Pie
              data={pieData}
              innerRadius={60}
              outerRadius={100}
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h2>Orders Trend</h2>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="More"
              actions={[
                {
                  label: 'View Orders Trend',
                  icon: 'view',
                  onClick: handleOrdersTrendMore,
                  tooltip: 'View detailed orders trend'
                },
                {
                  label: 'Export Data',
                  icon: 'download',
                  onClick: () => handleDownloadReport('orders-trend'),
                  tooltip: 'Export orders trend data'
                }
              ]}
            />
          </div>
          <div className="traffic-stats">
            <div className="traffic-stat">
              <span>Completed Orders</span>
              <div>
                <span>{safeLocale(dashboardData.completedOrders)}</span>
                <span className="change positive">+{percent(dashboardData.completedOrders, dashboardData.orders)}%</span>
              </div>
            </div>
            <div className="traffic-stat">
              <span>Pending Orders</span>
              <div>
                <span>{safeLocale(dashboardData.pendingOrders)}</span>
                <span className="change negative">-{percent(dashboardData.pendingOrders, dashboardData.orders)}%</span>
              </div>
            </div>
          </div>
          <LineChart width={500} height={200} data={trafficData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke="#FFA500"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </div>
      </div>

      {/* Additional Analytics Section */}
      <div className="additional-stats">
        <div className="stat-row">
          <div className="stat-item">
            <h3>Platform Overview</h3>
            <div className="stat-grid">
              <div className="mini-stat">
                <span className="label">Total Vendors</span>
                <span className="value">{safeLocale(dashboardData.allVendors)}</span>
              </div>
              <div className="mini-stat">
                <span className="label">Total Buyers</span>
                <span className="value">{safeLocale(dashboardData.allBuyers)}</span>
              </div>
              <div className="mini-stat">
                <span className="label">Staff Members</span>
                <span className="value">{safeLocale(dashboardData.allStaff)}</span>
              </div>
              <div className="mini-stat">
                <span className="label">Avg Order Value</span>
                <span className="value">{formatCurrency(dashboardData.averageOrderValue)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Tables Section */}
      <div className="tables-section">
        <div className="table-container">
          <div className="table-header">
            <h3>Recent Orders</h3>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="Export"
              actions={[
                {
                  label: 'Export Orders',
                  icon: 'download',
                  onClick: () => handleDownloadReport('recent-orders'),
                  tooltip: 'Export orders data'
                }
              ]}
            />
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(dashboardData.recentOrders) && dashboardData.recentOrders.length > 0 ? (
                  dashboardData.recentOrders.map((order, index) => (
                    <tr key={index}>
                      <td>{order?.id ?? 'N/A'}</td>
                      <td>{order?.customer ?? 'N/A'}</td>
                      <td>{order?.product ?? 'N/A'}</td>
                      <td>{formatCurrency(order?.amount)}</td>
                      <td>
                        <span className={`status-badge ${String(order?.status || 'pending').toLowerCase()}`}>
                          {order?.status || 'PENDING'}
                        </span>
                      </td>
                      <td>{order?.date || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="no-data">No recent orders available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="table-container">
          <div className="table-header">
            <h3>Top Products</h3>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="Export"
              actions={[
                {
                  label: 'Export Products',
                  icon: 'download',
                  onClick: () => handleDownloadReport('top-products'),
                  tooltip: 'Export products data'
                }
              ]}
            />
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Sales</th>
                  <th>Revenue</th>
                  <th>Growth</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(dashboardData.topProducts) && dashboardData.topProducts.length > 0 ? (
                  dashboardData.topProducts.map((product, index) => (
                    <tr key={index}>
                      <td>{product?.name || 'N/A'}</td>
                      <td>{safeLocale(product?.sales)}</td>
                      <td>{formatCurrency(product?.revenue)}</td>
                      <td>
                        <span className={`growth-badge ${toNumber(product?.growth) > 0 ? 'positive' : 'negative'}`}>
                          {toNumber(product?.growth) > 0 ? '+' : ''}{toNumber(product?.growth)}%
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">No top products available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="table-container">
          <div className="table-header">
            <h3>Vendor Performance</h3>
            <ShadcnActionDropdown
              size="small"
              variant="default"
              triggerText="Export"
              actions={[
                {
                  label: 'Export Vendors',
                  icon: 'download',
                  onClick: () => handleDownloadReport('vendor-performance'),
                  tooltip: 'Export vendors data'
                }
              ]}
            />
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Orders</th>
                  <th>Revenue</th>
                  <th>Rating</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(dashboardData.vendorPerformance) && dashboardData.vendorPerformance.length > 0 ? (
                  dashboardData.vendorPerformance.map((vendor, index) => (
                    <tr key={index}>
                      <td>{vendor?.name || 'N/A'}</td>
                      <td>{safeLocale(vendor?.orders)}</td>
                      <td>{formatCurrency(vendor?.revenue)}</td>
                      <td>
                        <span className="rating-badge">
                          ⭐ {vendor.rating}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${String(vendor?.status || 'active').toLowerCase()}`}>
                          {vendor?.status || 'ACTIVE'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No vendor performance data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Advanced Report Modal */}
      {showAdvancedReport && (
        <div className="modal-overlay" onClick={() => setShowAdvancedReport(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Advanced Revenue Report</h2>
              <button className="close-btn" onClick={() => setShowAdvancedReport(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="report-controls">
                <div className="date-range-selector">
                  <label>Date Range:</label>
                  <select value={selectedDateRange} onChange={(e) => handleDateRangeChange(e.target.value)}>
                    <option value="week">Last Week</option>
                    <option value="month">Last Month</option>
                    <option value="quarter">Last Quarter</option>
                    <option value="year">Last Year</option>
                  </select>
                </div>
                <button className="download-btn" onClick={() => handleDownloadReport('revenue')}>
                  <MdDownload /> Download CSV
                </button>
              </div>
              <div className="detailed-chart">
                <BarChart width={700} height={400} data={barData}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Bar dataKey="value" fill="#FFA500" />
                </BarChart>
              </div>
              <div className="report-summary">
                <h3>Summary</h3>
                <div className="summary-stats">
                  <div className="summary-stat">
                    <span>Total Revenue:</span>
                    <span>{formatCurrency(dashboardData.totalRevenue)}</span>
                  </div>
                  <div className="summary-stat">
                    <span>Average Daily Revenue:</span>
                    <span>{formatCurrency((toNumber(dashboardData.totalRevenue) || 0) / 30)}</span>
                  </div>
                  <div className="summary-stat">
                    <span>Growth Rate:</span>
                    <span className="positive">+{percent(dashboardData.monthlyRevenue, dashboardData.totalRevenue)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Device Traffic Modal */}
      {showDeviceTrafficModal && (
        <div className="modal-overlay" onClick={() => setShowDeviceTrafficModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Device Traffic Analysis</h2>
              <button className="close-btn" onClick={() => setShowDeviceTrafficModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="traffic-details">
                <PieChart width={400} height={400} data={pieData}>
                  <Pie
                    data={pieData}
                    innerRadius={80}
                    outerRadius={150}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
                <div className="traffic-breakdown">
                  <h3>Traffic Breakdown</h3>
                  {pieData.map((item, index) => (
                    <div key={index} className="traffic-item">
                      <div className="traffic-color" style={{ backgroundColor: item.color }}></div>
                      <span>{item.name}</span>
                      <span>{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Orders Trend Modal */}
      {showOrdersTrendModal && (
        <div className="modal-overlay" onClick={() => setShowOrdersTrendModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Orders Trend Analysis</h2>
              <button className="close-btn" onClick={() => setShowOrdersTrendModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="orders-controls">
                <button className="download-btn" onClick={() => handleDownloadReport('orders')}>
                  <MdDownload /> Download Orders Data
                </button>
              </div>
              <div className="detailed-orders-chart">
                <LineChart width={700} height={400} data={trafficData}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FFA500"
                    strokeWidth={3}
                    dot={{ fill: '#FFA500', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </div>
              <div className="orders-summary">
                <h3>Orders Summary</h3>
                <div className="orders-stats">
                  <div className="order-stat">
                    <span>Total Orders:</span>
                    <span>{safeLocale(dashboardData.orders)}</span>
                  </div>
                  <div className="order-stat">
                    <span>Completed Orders:</span>
                    <span>{safeLocale(dashboardData.completedOrders)}</span>
                  </div>
                  <div className="order-stat">
                    <span>Pending Orders:</span>
                    <span>{safeLocale(dashboardData.pendingOrders)}</span>
                  </div>
                  <div className="order-stat">
                    <span>Completion Rate:</span>
                    <span className="positive">{percent(dashboardData.completedOrders, dashboardData.orders)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Analytics Modal */}
      {showCartAnalyticsModal && (
        <div className="modal-overlay" onClick={() => setShowCartAnalyticsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cart Analytics Details</h2>
              <button className="close-btn" onClick={() => setShowCartAnalyticsModal(false)}>
                <MdClose />
              </button>
            </div>
            <div className="modal-body">
              <div className="cart-analytics-overview">
                <div className="conversion-rate-card">
                  <h3>Conversion Rate</h3>
                  <div className="big-percentage">
                    {safeFixed(dashboardData.conversionRate, 1)}%
                  </div>
                  <p>Overall cart to purchase conversion rate</p>
                </div>

                <div className="cart-metrics">
                  <div className="metric-card">
                    <h4>Abandoned Carts</h4>
                    <div className="metric-value">{safeLocale(dashboardData.abandonedCarts)}</div>
                    <div className="metric-description">Carts left without purchase</div>
                  </div>

                  <div className="metric-card">
                    <h4>Abandoned Revenue</h4>
                    <div className="metric-value">{formatCurrency(dashboardData.abandonedRevenue)}</div>
                    <div className="metric-description">Potential revenue lost</div>
                  </div>

                  <div className="metric-card">
                    <h4>Average Cart Value</h4>
                    <div className="metric-value">{formatCurrency(dashboardData.averageOrderValue)}</div>
                    <div className="metric-description">Typical cart value</div>
                  </div>

                  <div className="metric-card">
                    <h4>Recovery Rate</h4>
                    <div className="metric-value">{safeFixed(100 - toNumber(dashboardData.conversionRate), 1)}%</div>
                    <div className="metric-description">Abandonment rate</div>
                  </div>
                </div>
              </div>

              <div className="cart-recommendations">
                <h3>Recommendations</h3>
                <div className="recommendation-list">
                  <div className="recommendation-item">
                    <span className="recommendation-icon">💡</span>
                    <span>Implement abandoned cart recovery emails</span>
                  </div>
                  <div className="recommendation-item">
                    <span className="recommendation-icon">💡</span>
                    <span>Add exit-intent popups for cart abandonment</span>
                  </div>
                  <div className="recommendation-item">
                    <span className="recommendation-icon">💡</span>
                    <span>Simplify checkout process to reduce friction</span>
                  </div>
                  <div className="recommendation-item">
                    <span className="recommendation-icon">💡</span>
                    <span>Offer free shipping for higher cart values</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
